import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Seo from '@/components/base/Seo';
import NewsletterForm from '@/components/feature/NewsletterForm';

const PRINCIPLES = [
  { icon: 'ri-scales-3-line', title: '并列呈现不同立场', desc: '不替任何一方下结论，把支持、保留与观望的声音放在同一张页面上，让你自己判断。' },
  { icon: 'ri-links-line', title: '给出来源与脉络', desc: '每个话题都提供事件概述与讨论节点，避免只看到被截取的一句话而误解来龙去脉。' },
  { icon: 'ri-shield-check-line', title: '不放大未经证实的信息', desc: '对尚未有明确进展的说法保持克制标注，宁可更新慢一点，也不传播没把握的内容。' },
  { icon: 'ri-emotion-happy-line', title: '保持克制的表达', desc: '讨论可以热闹，但不鼓励攻击与站队对立，让吃瓜回到了解信息这件事本身。' },
];

const MILESTONES = [
  { year: '起步', title: '从整理评论区开始', desc: '最初只是想帮朋友快速搞懂一个热点在吵什么，于是开始手动整理高赞评论和主要立场。' },
  { year: '成形', title: '温度榜与立场分布', desc: '把热度、参与人数与立场比例做成可视化，一眼就能看出讨论规模和情绪走向。' },
  { year: '现在', title: '覆盖六个常驻分区', desc: '从娱乐、影视到社会、科技，形成六个常驻分区，每天 09:00 更新当日热榜。' },
];

export default function About() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 | 网报吃瓜',
      url: 'https://yuanfenq.cn/about',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '网报吃瓜',
      url: 'https://yuanfenq.cn',
      email: 'hello@wangbaochigua.com',
      description: '汇集网络热点评论、网友热议与不同立场的舆情观察站。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yuanfenq.cn' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://yuanfenq.cn/about' },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 | 网报吃瓜内容原则与联系"
        description="网报吃瓜是一个把网络热点评论、网友热议与不同立场聚在一起的舆情观察站。了解我们的站点定位、发展理念、内容原则与联系方式。"
        keywords="网报吃瓜,网报吃瓜介绍,内容原则,舆情观察,网友热议"
        path="/about"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="relative w-full py-14 md:py-20 bg-secondary-900">
          <div className="w-full px-4 md:px-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-background-50 max-w-3xl leading-snug">
              网报吃瓜：把纷乱的讨论，整理成看得懂的风向
            </h1>
            <p className="mt-5 text-sm md:text-lg text-background-200/85 max-w-2xl leading-relaxed">
              我们不生产八卦，只负责把已经发生的讨论摆整齐。让你在点开一个热点之前，先知道大家在争什么、为什么争。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">我们是谁</h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-sm md:text-base text-foreground-700 leading-loose">
              <p>
                网报吃瓜是一个面向普通网友的舆情观察站。每天有太多热点同时发生，评论散落在各个平台，
                想要完整了解一件事，往往要翻十几条时间线。我们把网友评论、热议角度、讨论走向和大众态度集中整理，
                让「跟上讨论」这件事变得简单一点。
              </p>
              <p>
                我们希望它既不是标题党，也不是冷冰冰的数据面板。你会看到温度榜告诉你哪个话题正在升温，
                也会看到立场分布告诉你评论区大概分成几派，还能顺着评论流读到具体的观点。
              </p>
              <p>
                它适合晚饭后想刷两条热点的你，也适合需要快速了解舆论走向的内容工作者。我们不承诺给你答案，
                但尽量把问题摆清楚。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-10">
            <div className="max-w-3xl">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">内容原则</h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                这四条是我们整理每一条话题时都会遵守的底线，也是你判断这里内容是否值得看的依据。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {PRINCIPLES.map((item) => (
                <div key={item.title} className="flex gap-4 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/80">
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">我们这样走到现在</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {MILESTONES.map((item, index) => (
                <div key={item.title} className="p-6 rounded-lg bg-accent-50 border border-accent-200/70">
                  <span className="inline-flex items-center gap-2 text-xs font-bold text-accent-800">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                      {index + 1}
                    </span>
                    {item.year}
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                如果你发现整理有误、想补充新的讨论角度，或希望我们关注某个话题，都欢迎来信。我们通常在两个工作日内回复。
              </p>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/80">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500 text-xs">邮箱</span>
                    <a href="mailto:hello@wangbaochigua.com" className="text-foreground-900 font-medium hover:text-primary-600 transition-colors cursor-pointer">
                      hello@wangbaochigua.com
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/80">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500 text-xs">更新时间</span>
                    <span className="text-foreground-900 font-medium">每日 09:00 更新热榜</span>
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <div className="p-6 md:p-8 rounded-lg bg-secondary-900">
                <h3 className="font-heading text-xl font-bold text-background-50">订阅热点周报</h3>
                <p className="mt-3 text-sm text-background-200/80 leading-relaxed">
                  每周挑出讨论最集中的话题与立场变化，整理成一封简短的邮件。只发热点，不发广告。
                </p>
                <div className="mt-6 bg-background-50 rounded-lg p-5">
                  <NewsletterForm />
                </div>
                <Link
                  to="/topics"
                  className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
                >
                  先去广场看看 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}